import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiX, FiExternalLink, FiGrid, FiList, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';
import exportAgendaToImage from '../utils/exportAgendaImage';

const isMc = (e) => (e.resource?.type || e.type) === 'masterclass';
const getCourseId = (e) => e.resource?.courseId || e.courseId || '_none';
const getGroupNumber = (e) => e.groupNumber || e.resource?.groupNumber || 1;

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
const fmtWeekday = (d) =>
  new Date(d).toLocaleDateString('es-PE', { weekday: 'short' });
const fmtTime = (d) =>
  new Date(d).toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const sanitize = (name) =>
  String(name || '')
    .normalize('NFKD')
    .replace(/[^\w\d-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const AgendaExpandModal = ({ open, onClose, instructor, courses = [], events = [], isGroupInactive }) => {
  const [viewMode, setViewMode] = useState('cajas'); // 'cajas' | 'lista'
  const capturableRef = useRef(null);

  // Cerrar con Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Filtra eventos cuyo grupo está marcado como inactivo (a nivel curso)
  const filteredEvents = useMemo(() => {
    if (!isGroupInactive) return events;
    return events.filter(e => !isGroupInactive(getCourseId(e), getGroupNumber(e)));
  }, [events, isGroupInactive]);

  // Agrupar eventos por curso
  const byCourse = useMemo(() => {
    const m = new Map();
    for (const e of filteredEvents) {
      const cId = getCourseId(e);
      if (!m.has(cId)) m.set(cId, []);
      m.get(cId).push(e);
    }
    return m;
  }, [filteredEvents]);

  const mcCount = useMemo(() => filteredEvents.filter(isMc).length, [filteredEvents]);
  const clCount = filteredEvents.length - mcCount;

  if (!open) return null;

  const handleExportImage = () => {
    const today = new Date().toISOString().slice(0, 10);
    const fileName = `Agenda_${sanitize(instructor?.name || 'Instructor')}_${today}.png`;
    exportAgendaToImage(capturableRef.current, fileName, toast);
  };

  const handleOpenPublic = () => {
    if (!instructor?.id) return;
    window.open(`/mi-calendario?id=${instructor.id}`, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-[92vw] max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          {instructor?.photoURL ? (
            <img src={instructor.photoURL} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: instructor?.color || '#6366f1' }}
            >
              {(instructor?.name || '?')[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-800 truncate">{instructor?.name || 'Instructor'}</h3>
            <p className="text-[11px] text-gray-500">
              {filteredEvents.length} eventos · <span className="text-purple-600 font-medium">{mcCount} MC</span> ·{' '}
              <span className="text-blue-600 font-medium">{clCount} CL</span>
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white/70 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => setViewMode('cajas')}
              className={`px-2 py-1 text-[11px] rounded-md font-medium flex items-center gap-1 transition ${
                viewMode === 'cajas' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Vista en cajas"
            >
              <FiGrid size={11} /> Cajas
            </button>
            <button
              onClick={() => setViewMode('lista')}
              className={`px-2 py-1 text-[11px] rounded-md font-medium flex items-center gap-1 transition ${
                viewMode === 'lista' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title="Vista en lista"
            >
              <FiList size={11} /> Lista
            </button>
          </div>
          <button
            onClick={handleExportImage}
            disabled={filteredEvents.length === 0}
            className="px-2.5 py-1.5 text-[11px] rounded-lg font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1"
            title="Exportar imagen PNG"
          >
            <FiImage size={12} /> Imagen
          </button>
          <button
            onClick={handleOpenPublic}
            disabled={!instructor?.id}
            className="px-2.5 py-1.5 text-[11px] rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1"
            title="Abrir dashboard del instructor"
          >
            <FiExternalLink size={12} /> Página
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Body capturable */}
        <div ref={capturableRef} className="flex-1 overflow-y-auto bg-white p-5">
          {filteredEvents.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm">Sin eventos</p>
          ) : viewMode === 'cajas' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...byCourse.entries()].map(([cId, evs]) => {
                const course = courses.find((c) => c.id === cId);
                const name = course?.name || course?.title || 'Sin curso';
                const byGrp = new Map();
                evs.forEach((e) => {
                  const g = getGroupNumber(e);
                  if (!byGrp.has(g)) byGrp.set(g, []);
                  byGrp.get(g).push(e);
                });
                const grpNums = [...byGrp.keys()].sort((a, b) => a - b);
                const mc = evs.filter(isMc).length;
                const cl = evs.length - mc;
                return (
                  <div key={cId} className="border border-gray-200 rounded-xl p-3 bg-gray-50/50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-xs font-bold text-gray-800 truncate flex-1">{name}</h4>
                      <div className="flex gap-1 shrink-0">
                        <span className="text-[9px] font-medium text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full">
                          {mc} MC
                        </span>
                        <span className="text-[9px] font-medium text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">
                          {cl} CL
                        </span>
                      </div>
                    </div>
                    {grpNums.map((g) => {
                      const list = byGrp.get(g).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
                      let mcN = 0;
                      let clN = 0;
                      return (
                        <div key={g} className="mb-2 last:mb-0">
                          {grpNums.length > 1 && (
                            <p className="text-[9px] font-bold text-indigo-600 uppercase mb-1">Grupo {g}</p>
                          )}
                          <div className="flex flex-wrap gap-1">
                            {list.map((ev) => {
                              const mc2 = isMc(ev);
                              const cancelled = ev.status === 'cancelled';
                              const n = cancelled ? '✕' : (mc2 ? ++mcN : ++clN);
                              return (
                                <span
                                  key={ev.id}
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    cancelled
                                      ? 'bg-red-50 text-red-400 line-through'
                                      : mc2
                                      ? 'bg-purple-50 text-purple-700'
                                      : 'bg-blue-50 text-blue-700'
                                  }`}
                                  title={`${fmtDate(ev.start)} ${fmtTime(ev.start)}`}
                                >
                                  {mc2 ? 'MC' : 'CL'} {n} · {fmtDate(ev.start)}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-4">
              {[...byCourse.entries()].map(([cId, evs]) => {
                const course = courses.find((c) => c.id === cId);
                const name = course?.name || course?.title || 'Sin curso';
                const byGrp = new Map();
                evs.forEach((e) => {
                  const g = getGroupNumber(e);
                  if (!byGrp.has(g)) byGrp.set(g, []);
                  byGrp.get(g).push(e);
                });
                const grpNums = [...byGrp.keys()].sort((a, b) => a - b);
                return (
                  <div key={cId}>
                    <h4 className="text-sm font-bold text-gray-800 mb-2 border-b border-gray-200 pb-1">
                      {name}
                    </h4>
                    {grpNums.map((g) => {
                      const list = byGrp.get(g).slice().sort((a, b) => new Date(a.start) - new Date(b.start));
                      let mcN = 0;
                      let clN = 0;
                      return (
                        <div key={g} className="mb-3">
                          {grpNums.length > 1 && (
                            <p className="text-[11px] font-bold text-indigo-600 uppercase mb-1">Grupo {g}</p>
                          )}
                          <table className="w-full text-[12px]">
                            <tbody>
                              {list.map((ev, idx) => {
                                const mc2 = isMc(ev);
                                const cancelled = ev.status === 'cancelled';
                                const n = cancelled ? '✕' : (mc2 ? ++mcN : ++clN);
                                return (
                                  <tr
                                    key={ev.id}
                                    className={`border-t border-gray-100 ${cancelled ? 'opacity-40 line-through' : ''} ${idx % 2 ? 'bg-gray-50/50' : 'bg-white'}`}
                                  >
                                    <td className="px-2 py-1.5 font-medium text-gray-800 whitespace-nowrap w-32">
                                      <div>{fmtDate(ev.start)}</div>
                                      <div className="text-[10px] text-gray-400 capitalize">{fmtWeekday(ev.start)}</div>
                                    </td>
                                    <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap w-36">
                                      {fmtTime(ev.start)} – {fmtTime(ev.end)}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                          mc2 ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                        }`}
                                      >
                                        {mc2 ? `MC ${n}` : `CL ${n}`}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgendaExpandModal;
